// This script handles:
// - Offering the user a redirect if they geolocate to a country unsuited to the domain.
// - Setting the user's currency automatically to their geolocated region unless it has been set explicitly.
// - ? Reinstating a user's currency after having been to the AUD checkout.

import { encode } from 'safe-base64';

import { spawnDialog } from './dialogs';
import { overlayToggle } from './overlay';
import { setCurrency, flagAsCurrencied } from './currency';
import getParamByName from './utilities/getParamByName';
import { removeParams } from './utilities/utils';
const nowTime = new Date().getTime();
const currentRegionCode = window.theme.regionCode;
const currentDomain = window.location.hostname;
const { cartCurrency: currentCurrency } = window.theme;

const loccedParamFound = getParamByName('locced');
if (loccedParamFound) {
  window.localStorage.setItem('locced', nowTime);
  removeParams(['locced']);
}
const locced = window.localStorage.getItem('locced');

const EUR_COUNTRIES = [
  'AT', // Austria
  'BE', // Belgium
  'FI', // Finland
  'FR', // France
  'DE', // Germany
  'GR', // Greece
  'IE', // Ireland
  'IT', // Italy
  'LT', // Lithuania
  'LU', // Luxembourg
  'MT', // Malta
  'NL', // Netherlands
  'PT', // Portugal
  'SI', // Slovenia
  'ES', // Spain
];

const GBP_COUNTRIES = [
  'GB', // Great Britain
  'GG', // Guernsey
  'JE', // Jersey
  'IM', // Isle of Man
];

const UAE_COUNTRIES = [
  'AE', // United Arab Emirates
  'SA', // Saudi Arabia
];

const STORE_MAP = {

  au_store: {
    domain: 'whitefoxboutique.com.au',
    sites: {
      au: {
        currency: 'AUD',
        // domain: 'whitefoxboutique.com.au',
        countryCodes: [
          'AU',
          'ROW',
        ],
      },
      nz: {
        currency: 'NZD',
        // domain: 'whitefoxboutique.com.au',
        countryCodes: [
          'NZ',
        ],
      },
      ca: {
        currency: 'CAD',
        domain: 'whitefoxboutique.ca',
        countryCodes: ['CA'],
      },
      // mx: {
      //   currency: 'MXN',
      //   // domain: 'whitefoxboutique.com.au',
      //   countryCodes: ['MX'],
      // },
    },
  },

  us_store: {
    domain: 'whitefoxboutique.com',
    sites: {
      us: {
        currency: 'USD',
        countryCodes: ['US'],
      },
    },
  },

  uk_store: {
    domain: 'whitefoxboutique.co.uk',
    sites: {
      uk: {
        currency: 'GBP',
        countryCodes: [...GBP_COUNTRIES],
      },
      eu: {
        currency: 'EUR',
        countryCodes: [...EUR_COUNTRIES],
      },
      ae: {
        currency: 'AED',
        countryCodes: [...UAE_COUNTRIES],
      },
    },
  },
};

// const onStagingUrl = window.location.host.includes('myshopify.com');

const siteByCountryCode = (countryCode) => {
  for (const [storeKey, store] of Object.entries(STORE_MAP)) {
    for (const [siteKey, site] of Object.entries(store.sites)) {
      if (site.countryCodes.includes(countryCode)) {
        return {
          domain: store.domain,
          ...site,
        };
      }
    }
  }

  return false;
};

const siteByCountryCodeOrDefault = (countryCode) => {
  return siteByCountryCode(countryCode) || siteByCountryCode('ROW');
};

const siteByCurrencyOrDefault = (currency) => {
  for (const [storeKey, store] of Object.entries(STORE_MAP)) {
    for (const [siteKey, site] of Object.entries(store.sites)) {
      if (site.currency === currency) {
        return {
          domain: store.domain,
          ...site,
        };
      }
    }
  }

  return siteByCountryCode('ROW');
};

// siteByCountryCodeOrDefault('US')
// siteByCountryCodeOrDefault('SA')
// siteByCountryCodeOrDefault('NZ')
// siteByCountryCodeOrDefault('ZW')

let currentSite;
for (const [storeKey, store] of Object.entries(STORE_MAP)) {
  let domain = store.domain;
  for (const [siteKey, site] of Object.entries(store.sites)) {

    const {
      countryCodes,
      currency,
    } = site;
    domain = site?.domain || domain;

    if (domain === currentDomain && currency === currentCurrency) {
      currentSite = {
        domain: store.domain,
        ...site,
      };
    }
  }
}
// console.log('currentSite', currentSite);

const redirectToSite = (site, { returnInsteadOfAction = false } = {}) => {

  const { domain, currency } = site;

  if (!domain) {
    console.log('Redirect failed, no domain', site);
    return;
  }

  const url = new URL(location.href);
  url.host = domain;
  url.searchParams.set('currency', currency);
  window?.geolocator?.data && url.searchParams.set('geo', encode(Buffer.from(JSON.stringify(window.geolocator.data))));
  url.searchParams.set('locced', true);
  
  if (returnInsteadOfAction) {
    return url.href;
  }

  window.localStorage.setItem('locced', nowTime);

  if (site.domain === currentSite.domain && site.currency === currentCurrency) {
    console.log('Redirect failed, already on the site');
    return;
  }

  window.location = url.href;
};

const changeCurrency = (currency) => window.currencyUtils.setCurrency(currency);

const redirectOptionsMap = {
  'AUS & NZ': ['AU', 'NZ'],
  'Europe & UAE': [...EUR_COUNTRIES, ...UAE_COUNTRIES],
  'UK': [...GBP_COUNTRIES],
  'USA': ['US'],
  'Canada': ['CA'],
  'Rest of the World': ['ROW'],
};

const redirectOptionByCountryCode = (countryCode) => {
  return Object.entries(redirectOptionsMap).find(([key, value]) => value.includes(countryCode));
};

const redirectOptionByCountryCodeOrDefault = (countryCode) => {
  return redirectOptionByCountryCode(countryCode) || redirectOptionByCountryCode('ROW');
};

const offerRedirect = (userCountryCode) => {

  const recommendedSite = siteByCountryCode(userCountryCode);
  if (recommendedSite.domain === currentDomain && recommendedSite.currency === currentCurrency) {
    // At home
    return;
  }

  const [recommendedOptionTitle, recommendedCountryCodes] = redirectOptionByCountryCodeOrDefault(userCountryCode);

  const redirectOptionsObj = {
    [recommendedOptionTitle]: () => redirectToSite(siteByCountryCode(recommendedCountryCodes[0])),
    ...Object.fromEntries(Object.entries(redirectOptionsMap)
      .filter(([key]) => key !== recommendedOptionTitle)
      .map(([key, value]) => [key, () => redirectToSite(siteByCountryCode(value[0]))])
    ),
  };
  console.log('redirectOptionsObj', redirectOptionsObj);

  const redirectCloseCallback = () => {
    window.localStorage.setItem('locced', nowTime);
    overlayToggle(false);
  };

  const overlayCloseCallback = () => {
    redirectDialog.close();
  };

  const redirectDialog = spawnDialog(
    `<h2>Welcome!<br/>Are you in the right place?</h2>Please select a store:`,
    'redirect',
    redirectOptionsObj,
    redirectCloseCallback
  );

  overlayToggle(true, overlayCloseCallback);
};

const editFreeShippingThresholdsConfig = (countryCode) => {
  const { free_shipping_thresholds, free_shipping_thresholds_ready } = window.config;
  if (free_shipping_thresholds_ready) {
    return;
  }

  const countryCodeThresholds = free_shipping_thresholds.filter(threshold => threshold?.country_codes?.includes(countryCode));
  if (countryCodeThresholds.length > 0) {
    window.config.free_shipping_thresholds = countryCodeThresholds;
    window.config.free_shipping_thresholds_ready = true;
    return;
  }

  const thresholdsWithoutCountryCodes = free_shipping_thresholds.filter(threshold => !threshold?.country_codes);
  window.config.free_shipping_thresholds = thresholdsWithoutCountryCodes;
  window.config.free_shipping_thresholds_ready = true;
};

const trigger = (geoInfo) => {
  console.log('regionalise trigger', geoInfo);
  const { country_code: userCountryCode } = geoInfo;
  offerRedirect(userCountryCode);
  editFreeShippingThresholdsConfig(userCountryCode);
};

if (!locced) {
  window.eventKeeper.onEventHeard('geolocator:announce', trigger);
}

const regionCodeToStoreMap = {
  'au': 'au_store',
  'nz': 'au_store',
  'ca': 'au_store',
  'us': 'us_store',
  'uk': 'uk_store',
  'eu': 'uk_store',
  'ae': 'uk_store',
};

const switchRegion = (regionCode, currency) => {
  const storeKey = regionCodeToStoreMap[regionCode];
  const store = STORE_MAP[storeKey];
  let site = Object.values(store.sites).find(site => site.currency === currency) || store.sites[regionCode];
  site = {
    domain: store.domain,
    ...site,
  };
  redirectToSite(site);
};

const switchLocale = (countryCode) => {
  console.log('switchLocale', countryCode);
  const site = siteByCountryCodeOrDefault(countryCode);
  redirectToSite(site);
};

const switchCurrency = (currency) => {
  console.log('switchCurrency', currency);
  const site = siteByCurrencyOrDefault(currency);
  redirectToSite(site);
};

window.switchRegion = switchRegion;
window.switchLocale = switchLocale;
window.switchCurrency = switchCurrency;
















// // If we were redirected from another region, with the ?locced= parameter, don't check the location again. The user is where they want to be.
// // const locced = getParamByName('locced');
// // if (locced) {
// //   window.localStorage.setItem('locced', nowTime);
// // }

// // const locationCheck = window.localStorage.getItem('locced');
// // const locationNeedsChecking = !locationCheck;

// // If there's a currency param in the url, don't override it
// // const currencied = getParamByName('currency');
// // if (currencied) {
// //   window.localStorage.setItem('currencied', nowTime);
// // }

// // const currencyChosen = window.localStorage.getItem('currencied');
// // const currencyNeedsChecking = !currencyChosen;


// // Redirect planning

// // on page load
// // if param {
// //   record cookie
// // }

// // If no cookie set {
// //   IPAPI > settings.region code {
// //     show redirect popup
// //     on choice {
// //       record cookie
// //       send to other site with param
// //     }
// //   }
// // }

// // cookie lasts 10 minutes

// const recordAndRedirect = (newDomain, currency) => {
//   console.log('recordAndRedirect', newDomain, currency);
//   window.localStorage.setItem('locced', nowTime);

//   // Don't redirect if already on the new domain
//   if (domain === newDomain && currentCurrency === currency) {
//     overlayToggle(false);
//     return;
//   }

//   window.location = redirectLocation(newDomain, currency);
// };

// // async wrapper to allow await
// (async () => {
//   // If the location does not need checking, don't call IPAPI, but use a stored location to determine currency if available.
//   const userLocationInfo = await getUserLocation(locationNeedsChecking);
//   const country_code = userLocationInfo?.country_code;

//   editFreeShippingThresholdsConfig(country_code);

//   const noRedirectCallback = () => {
//     if (!currencyNeedsChecking) {
//       console.log(`Currency doesn't need checking`);
//       return;
//     }
//     console.log(`Currency needs checking`);
//     checkAndSetCurrency(country_code);
//   };

//   if (locationNeedsChecking) {
//     console.log(`Location needs checking`);
//     offerRedirect(noRedirectCallback, country_code);
//     return;
//   } 
    
//   console.log(`Location doesn't need checking`);
//   noRedirectCallback();
// })();

// // If you have just been relocated, check the currency and set it to the appropriate currency for your choice.
// // If you make an explicit currency choice, record a localStorage item and do not set it again.
// // If you are not relocated and your currency doesn't match the store currency, set the currency to the store currency, and set the item so it does not automatically switch again.

// const switchSite = (siteCode, currency) => {
//   console.log('switchSite', siteCode, currency);
//   // This function switches between the big sites in the BIG_SITE_CONFIG.

//   console.log(`Switch to ${ siteCode }`);

//   // Thanks, EU
//   const toSite = BIG_SITE_CONFIG[siteCode];

//   if (siteCode === CURRENT_SITE.code && toSite.ownedCurrencies.includes(currentCurrency)) {
//     console.warn(`Already on ${ siteCode }`);
//     return;
//   }

//   // Only allow switching to owned currencies
//   const fallbackCurrency = toSite.defaultCurrency;
//   const requestedCurrency = currency || currentCurrency;
//   const toCurrency = toSite.ownedCurrencies.includes(requestedCurrency) ? requestedCurrency : fallbackCurrency;
  
//   recordAndRedirect(toSite.domain, toCurrency);
// };

// const switchLocale = (countryCode) => {
//   console.log('switchLocale', countryCode);
//   const countryCurrency = currencyFromCountryCode(countryCode);
//   if (!countryCurrency) {
//     console.error(`No currency found for country ${ countryCode }`);
//     return;
//   }
//   switchCurrency(countryCurrency);
// };

// const switchCurrency = (currency) => {
//   console.log('switchCurrency', currency);
//   const currencySite = Object.values(BIG_SITE_CONFIG).find(v => v.ownedCurrencies.includes(currency));
//   if (currencySite === CURRENT_SITE) {
//     // Change the currency with the endpoint
//     window.currencyUtils.setCurrency(currency);
//     return;
//   }

//   switchSite(currencySite.code, currency);
// };

// window.switchSite = switchSite;
// window.switchLocale = switchLocale;
// window.switchCurrency = switchCurrency;

// /*
// Remove stored location info
// window.localStorage.removeItem('locced');
// window.localStorage.removeItem('currencied');
// window.localStorage.removeItem('IPAPIInfo');
// window.localStorage.setItem('IPAPIInfo', JSON.stringify({ country_code: 'AE' }));

// Set location to UK for testing
// window.localStorage.setItem('IPAPIInfo', JSON.stringify({ country_code: 'GB' }));

// Set location to Ireland to test EU
// window.localStorage.setItem('IPAPIInfo', JSON.stringify({ country_code: 'IE' }));
// */
